import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, IconButton, InputAdornment, LinearProgress, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography, useTheme,
} from '@mui/material';
import { Clear, Groups as GroupsIcon, Search } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { peopleService, type FamilyListItem, type FamilyStatus } from '../services/peopleService';
import { useDebounce } from '../hooks/useDebounce';
import { ToneChip } from '../components/common/StatusChip';
import EmptyState from '../components/common/EmptyState';
import TableRowSkeletons from '../components/common/TableRowSkeletons';
import { tableHeadCellSx } from '../components/admissions/tabLayout';
import FamilyDrawer from '../components/families/FamilyDrawer';
import { FAMILY_STATUS, familyName } from '../components/families/familyFormat';
import dateTimeUtils from '../utils/dateTimeUtils';
import { grLabel } from '../utils/personRef';

type StatusFilter = 'all' | FamilyStatus;

/**
 * Families — every household the school knows: its children (applicants,
 * students, alumni) and guardians. Search by anything staff are likely to
 * know; open a family to manage its guardians or merge it with another
 * family that is really the same household.
 */
const FamiliesPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '');
  const search = useDebounce(searchInput, 300);
  const [status, setStatus] = useState<StatusFilter>((searchParams.get('status') as StatusFilter) || 'all');
  const [siblings, setSiblings] = useState(searchParams.get('siblings') === '1');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const openId = searchParams.get('family');

  const [rows, setRows] = useState<FamilyListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters and the open family live in the URL, so a view is linkable.
  const setParam = useCallback((key: string, value: string | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value); else next.delete(key);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  useEffect(() => { setParam('q', search.trim() || null); }, [search, setParam]);
  useEffect(() => { setParam('status', status === 'all' ? null : status); }, [status, setParam]);
  useEffect(() => { setParam('siblings', siblings ? '1' : null); }, [siblings, setParam]);
  useEffect(() => { setPage(0); }, [search, status, siblings]);

  // Only the newest request may fill the table.
  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const res = await peopleService.listFamilies({
        q: search.trim() || undefined,
        status: status === 'all' ? undefined : status,
        siblings: siblings || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      if (mine !== seq.current) return;
      setRows(res.items);
      setTotal(res.pagination.totalItems);
    } catch (err: any) {
      if (mine !== seq.current) return;
      enqueueSnackbar(err?.message || 'Could not load families', { variant: 'error' });
      setRows([]);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [search, status, siblings, page, rowsPerPage, enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  const filtersActive = !!search.trim() || status !== 'all' || siblings;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>Families</Typography>
        <Typography variant="body2" color="text.secondary">
          Every household — its children and guardians. Siblings belong in one family; merge families that turn out to be the same household.
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            size="small"
            placeholder="Search family, guardian, phone, CNIC, child or GR…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            sx={{ flex: 1, minWidth: 260 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput('')}><Clear fontSize="small" /></IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => v && setStatus(v)}>
            <ToggleButton value="all" sx={{ textTransform: 'none' }}>All</ToggleButton>
            <ToggleButton value="resolved" sx={{ textTransform: 'none' }}>{FAMILY_STATUS.resolved.label}</ToggleButton>
            <ToggleButton value="candidate" sx={{ textTransform: 'none' }}>{FAMILY_STATUS.candidate.label}</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButton size="small" value="siblings" selected={siblings} onChange={() => setSiblings(s => !s)} sx={{ textTransform: 'none' }}>
            Has siblings
          </ToggleButton>
        </Stack>

        {loading && rows.length > 0 && <LinearProgress sx={{ height: 2 }} />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': tableHeadCellSx }}>
                <TableCell>Family</TableCell>
                <TableCell>Children</TableCell>
                <TableCell>Guardians</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRowSkeletons columns={5} rows={8} />
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 6 }}>
                    <EmptyState
                      icon={<GroupsIcon />}
                      title={filtersActive ? 'No families match' : 'No families yet'}
                      description={filtersActive
                        ? 'Try another name, phone or GR, or clear the filters.'
                        : 'Families are created when an applicant applies or a student is enrolled.'}
                      action={filtersActive ? { label: 'Clear filters', onClick: () => { setSearchInput(''); setStatus('all'); setSiblings(false); } } : undefined}
                    />
                  </TableCell>
                </TableRow>
              ) : rows.map(f => (
                <TableRow key={f._id} hover sx={{ cursor: 'pointer' }} onClick={() => setParam('family', f._id)}>
                  <TableCell><Typography variant="body2" fontWeight={600}>{familyName(f)}</Typography></TableCell>
                  <TableCell>
                    {f.children.map(c => (
                      <Typography key={c._id} variant="body2" noWrap>
                        {c.name} <Box component="span" sx={{ color: 'text.secondary', fontFamily: c.grNumber ? 'monospace' : undefined, fontSize: '0.8rem' }}>{grLabel(c)}</Box>
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell>
                    {f.guardians.length === 0
                      ? <Typography variant="body2" color="text.disabled">Not recorded</Typography>
                      : f.guardians.map(g => (
                        <Typography key={g._id} variant="body2" noWrap>
                          {g.name} <Box component="span" sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>· {g.relation}</Box>
                          <Box component="span" sx={{ color: 'text.secondary' }}> · {g.phone}</Box>
                        </Typography>
                      ))}
                  </TableCell>
                  <TableCell><ToneChip label={FAMILY_STATUS[f.status].label} tone={FAMILY_STATUS[f.status].tone} /></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary" noWrap>{dateTimeUtils.formatDateForDisplay(f.updatedAt)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {openId && (
        <FamilyDrawer
          key={openId}
          familyId={openId}
          onClose={() => setParam('family', null)}
          onChanged={load}
          onOpenFamily={id => setParam('family', id)}
        />
      )}
    </Box>
  );
};

export default FamiliesPage;
